import { useState } from "react";
import { Button } from "@/components/ui/button";
import { X, AlertTriangle, RotateCcw } from "lucide-react";

const RollbackModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  checkpoint,
  currentState,
  rollbackCredits,
  loading 
}) => {
  if (!isOpen || !checkpoint) return null;

  const messagesRemoved = currentState?.messageCount - checkpoint.message_index;
  const xpChange = currentState?.xp - checkpoint.xp;
  const levelChange = currentState?.level - checkpoint.level;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-divine-charcoal border border-white/20 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-divine-gold" />
            <h3 className="text-lg font-semibold text-white">Rollback to this point?</h3>
          </div>
          <button 
            onClick={onClose}
            className="text-white/50 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Warning */}
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-orange-200">
              This will undo all messages and progress after this point. This action cannot be undone.
            </p>
          </div>
        </div>

        {/* Changes Preview */}
        <div className="space-y-2 mb-6">
          <h4 className="text-sm text-white/60 mb-2">This will undo:</h4>
          
          <div className="flex justify-between text-sm">
            <span className="text-white/70">Messages removed:</span>
            <span className="text-red-400">-{messagesRemoved || 0}</span>
          </div>
          
          {xpChange > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-white/70">XP change:</span>
              <span className="text-red-400">
                {currentState?.xp?.toFixed(0)} → {checkpoint.xp?.toFixed(0)} (-{xpChange?.toFixed(0)})
              </span>
            </div>
          )}
          
          {levelChange > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-white/70">Level change:</span>
              <span className="text-red-400">
                Level {currentState?.level} → Level {checkpoint.level}
              </span>
            </div>
          )}
          
          <div className="border-t border-white/10 pt-2 mt-2">
            <div className="flex justify-between text-sm">
              <span className="text-white/70">Rollback credits:</span>
              <span className="text-white">
                {rollbackCredits} → {rollbackCredits - 1}
              </span>
            </div>
          </div>
        </div>

        {/* Preview Message */}
        {checkpoint.preview_message && (
          <div className="bg-divine-black/50 rounded-lg p-3 mb-4">
            <p className="text-xs text-white/50 mb-1">Rollback to after:</p>
            <p className="text-sm text-white/80 italic">&quot;{checkpoint.preview_message}&quot;</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-white/20 text-white hover:bg-white/10"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            disabled={loading || rollbackCredits <= 0}
          >
            {loading ? (
              <>
                <RotateCcw className="w-4 h-4 mr-2 animate-spin" />
                Rolling back...
              </>
            ) : (
              <>
                <RotateCcw className="w-4 h-4 mr-2" />
                Confirm Rollback
              </>
            )}
          </Button>
        </div>

        {rollbackCredits <= 0 && (
          <p className="text-center text-red-400 text-xs mt-3">
            No rollback credits remaining
          </p>
        )}
      </div>
    </div>
  );
};

export default RollbackModal;
